import React, { useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Modal,
    Spinner,
} from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiEye,
    FiPlay,
    FiRefreshCw,
} from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { checkStandardScheduledBulkObservations } from '../../services/api';
import { formatRequestError } from '../../utils/errorHandler';
import {
    canRunStandardScheduledForecastImport,
    getAdminRoleFromToken,
} from '../../utils/ecmwfExcelImport';
import { useTranslation } from '../../hooks/useTranslation';
import { useLongRunningProgress, getLongWaitMessage } from '../../hooks/useLongRunningProgress';
import LongRunningOperationBanner from './LongRunningOperationBanner';
import './StandardCheckForecastTab.css';

const DEFAULT_PREFIX = 'pages.adminWeatherDailyAnalysis.standardBulkObservation';
const DEFAULT_ENDPOINT = '/api/standard/trips/scheduled/bulk-observation-check/grouped-by-location';

const parseBulkObservationError = (error, t, prefix) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    if (status === 403) {
        return {
            title: t(`${prefix}.errors.forbiddenTitle`),
            message: t(`${prefix}.errors.forbidden`),
        };
    }
    if (status === 404) {
        const apiMessage = detail && typeof detail === 'object' && detail.message
            ? String(detail.message)
            : (typeof detail === 'string' ? detail : null);
        return {
            title: t(`${prefix}.errors.notFoundTitle`),
            message: apiMessage || t(`${prefix}.errors.notFound`),
        };
    }
    if (status === 500) {
        return {
            title: t(`${prefix}.errors.serverTitle`),
            message: t(`${prefix}.errors.server`),
        };
    }

    if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.message) {
        return {
            title: t(`${prefix}.errors.genericTitle`),
            message: String(detail.message),
        };
    }
    if (typeof detail === 'string' && detail.trim()) {
        return {
            title: t(`${prefix}.errors.genericTitle`),
            message: detail,
        };
    }

    return {
        title: t(`${prefix}.errors.genericTitle`),
        message: formatRequestError(error, t(`${prefix}.error`), {
            timeout: t(`${prefix}.errors.timeout`),
            network: t(`${prefix}.errors.network`),
        }),
    };
};

const formatDataSources = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).join(', ') || '—';
    if (value == null || value === '') return null;
    return String(value);
};

const buildResultRows = (result, t, prefix) => {
    if (!result || typeof result !== 'object') return [];

    const summary = result.summary && typeof result.summary === 'object' ? result.summary : {};
    const checkObservations = result.check_observations && typeof result.check_observations === 'object'
        ? result.check_observations
        : {};
    const metadata = checkObservations.metadata && typeof checkObservations.metadata === 'object'
        ? checkObservations.metadata
        : (result.metadata && typeof result.metadata === 'object' ? result.metadata : {});

    const rows = [];

    if (result.trigger_type || summary.trigger_type) {
        rows.push({
            key: 'triggerType',
            label: t(`${prefix}.triggerType`),
            value: result.trigger_type || summary.trigger_type,
        });
    }

    const dataSources = formatDataSources(
        result.data_sources ?? summary.data_sources ?? metadata.data_sources,
    );
    if (dataSources) {
        rows.push({
            key: 'dataSources',
            label: t(`${prefix}.dataSources`),
            value: dataSources,
        });
    }

    if (summary.total_policies_processed != null) {
        rows.push({
            key: 'policiesProcessed',
            label: t(`${prefix}.policiesProcessed`),
            value: summary.total_policies_processed,
        });
    }
    if (summary.policies_triggered != null) {
        rows.push({
            key: 'policiesTriggered',
            label: t(`${prefix}.policiesTriggered`),
            value: summary.policies_triggered,
        });
    }
    if (summary.policies_not_triggered != null) {
        rows.push({
            key: 'policiesNotTriggered',
            label: t(`${prefix}.policiesNotTriggered`),
            value: summary.policies_not_triggered,
        });
    }
    if (summary.policies_with_errors != null) {
        rows.push({
            key: 'policiesWithErrors',
            label: t(`${prefix}.policiesWithErrors`),
            value: summary.policies_with_errors,
        });
    }
    if (summary.locations_failed != null || metadata.locations_failed != null) {
        rows.push({
            key: 'locationsFailed',
            label: t(`${prefix}.locationsFailed`),
            value: summary.locations_failed ?? metadata.locations_failed,
        });
    }

    const skippedMissing = summary.skipped_missing_obs_data_source_fallback
        ?? metadata.skipped_missing_obs_data_source_fallback;
    if (skippedMissing != null) {
        rows.push({
            key: 'skippedMissingFallback',
            label: t(`${prefix}.skippedMissingFallback`),
            value: skippedMissing,
        });
    }

    const skippedInvalid = summary.skipped_invalid_obs_data_source_fallback
        ?? metadata.skipped_invalid_obs_data_source_fallback;
    if (skippedInvalid != null) {
        rows.push({
            key: 'skippedInvalidFallback',
            label: t(`${prefix}.skippedInvalidFallback`),
            value: skippedInvalid,
        });
    }

    const observationHour = summary.observation_hour ?? metadata.observation_hour;
    if (observationHour != null && observationHour !== '') {
        rows.push({
            key: 'observationHour',
            label: t(`${prefix}.observationHour`),
            value: observationHour,
        });
    }

    const processingTime = summary.processing_time_ms ?? metadata.processing_time_ms;
    if (processingTime != null) {
        rows.push({
            key: 'processingTime',
            label: t(`${prefix}.processingTime`),
            value: `${Math.round(processingTime)} ms`,
        });
    }

    if (result.timestamp) {
        rows.push({
            key: 'timestamp',
            label: t(`${prefix}.timestamp`),
            value: result.timestamp,
        });
    }

    return rows;
};

const StandardBulkObservationCheckTab = ({
    prefix = DEFAULT_PREFIX,
    runRequest = checkStandardScheduledBulkObservations,
    sourceField = 'obs_data_source',
    endpointPath = DEFAULT_ENDPOINT,
}) => {
    const { t } = useTranslation();
    const PREFIX = prefix;
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const role = getAdminRoleFromToken(accessToken || localStorage.getItem('admin_access_token'));
    const canRun = canRunStandardScheduledForecastImport(role);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);

    const progressSteps = useMemo(() => ([
        t(`${PREFIX}.progress.step1`),
        t(`${PREFIX}.progress.step2`),
        t(`${PREFIX}.progress.step3`),
    ]), [t, PREFIX]);

    const { currentStep, elapsedSeconds } = useLongRunningProgress(loading, progressSteps);
    const longWaitNote = getLongWaitMessage(elapsedSeconds, {
        stillNormal: t(`${PREFIX}.progress.stillNormal`),
        long: t(`${PREFIX}.progress.longWait`),
        twoMinutes: t(`${PREFIX}.progress.twoMinutesWait`),
        veryLong: t(`${PREFIX}.progress.veryLongWait`),
        overFourMinutes: t(`${PREFIX}.progress.overFourMinutesWait`),
    });

    const isSuccess = result?.status === 'success' || result?.status_code === 200;
    const resultRows = useMemo(() => buildResultRows(result, t, PREFIX), [result, t, PREFIX]);

    const openConfirm = () => {
        if (loading || !canRun) return;
        setError(null);
        setShowConfirm(true);
    };

    const runCheck = async () => {
        if (loading || !canRun) return;
        setShowConfirm(false);
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const response = await runRequest();
            setResult(response);
        } catch (err) {
            if (err?.response?.status !== 401) {
                setError(parseBulkObservationError(err, t, PREFIX));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="standard-check-forecast-tab">
            <header className="scf-header">
                <h2 className="scf-title">{t(`${PREFIX}.title`)}</h2>
                <p className="scf-subtitle">{t(`${PREFIX}.description`)}</p>
                <p className="scf-source">
                    <span className="scf-source__label">{t(`${PREFIX}.sourceLabel`)}</span>
                    <code className="scf-source__value">{sourceField}</code>
                </p>
                <div className="fb-endpoint mt-2" role="note">
                    <span className="fb-endpoint__method">POST</span>
                    <code>{endpointPath}</code>
                </div>
            </header>

            {!canRun && (
                <Alert variant="warning" className="mb-3">
                    <FiAlertTriangle className="me-2" aria-hidden />
                    {t(`${PREFIX}.errors.forbidden`)}
                </Alert>
            )}

            <section className="scf-action">
                <div className="scf-action__info">
                    <div className="scf-action__ready">{t(`${PREFIX}.readyTitle`)}</div>
                </div>
                <Button
                    type="button"
                    className="btn scf-btn-primary"
                    onClick={openConfirm}
                    disabled={loading || !canRun}
                >
                    {loading ? (
                        <>
                            <Spinner size="sm" animation="border" aria-hidden />
                            {t(`${PREFIX}.running`)}
                        </>
                    ) : (
                        <>
                            <FiPlay size={16} aria-hidden />
                            {t(`${PREFIX}.runButton`)}
                        </>
                    )}
                </Button>
            </section>

            <LongRunningOperationBanner
                show={loading}
                sticky
                title={t(`${PREFIX}.progress.title`)}
                expectedDuration={t(`${PREFIX}.progress.expectedDuration`)}
                message={currentStep}
                elapsedSeconds={elapsedSeconds}
                elapsedLabel={t(`${PREFIX}.progress.elapsed`)}
                longWaitNote={longWaitNote}
                hint={t(`${PREFIX}.progress.hint`)}
            />

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="scf-alert">
                    <div className="d-flex align-items-start gap-2 mb-2">
                        <FiAlertTriangle className="flex-shrink-0 mt-1" aria-hidden />
                        <div>
                            <strong className="d-block mb-1">{error.title}</strong>
                            <span>{error.message}</span>
                        </div>
                    </div>
                    {canRun && (
                        <div className="text-end">
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={runCheck}
                                disabled={loading}
                            >
                                <FiRefreshCw size={14} className="me-1" aria-hidden />
                                {t(`${PREFIX}.retry`)}
                            </Button>
                        </div>
                    )}
                </Alert>
            )}

            {result && (
                <section className={`scf-result ${isSuccess ? 'is-success' : 'is-warning'}`}>
                    <div className="scf-result__header">
                        {isSuccess
                            ? <FiCheckCircle className="scf-result__icon" aria-hidden />
                            : <FiEye className="scf-result__icon" aria-hidden />}
                        <div>
                            <h3 className="scf-result__title">{t(`${PREFIX}.resultTitle`)}</h3>
                            <p className="scf-result__message mb-0">
                                {isSuccess
                                    ? (result.message || t(`${PREFIX}.successDefault`))
                                    : (result.message || t(`${PREFIX}.resultTitle`))}
                            </p>
                        </div>
                        {result.status && (
                            <Badge bg={isSuccess ? 'success' : 'secondary'} className="scf-result__status">
                                {result.status}
                            </Badge>
                        )}
                    </div>

                    {resultRows.length > 0 && (
                        <ul className="scf-result__list">
                            {resultRows.map((row) => (
                                <li key={row.key} className="scf-result__row">
                                    <FiCheckCircle className="scf-result__row-icon" aria-hidden />
                                    <span className="scf-result__row-label">{row.label}</span>
                                    <span className="scf-result__row-value">{row.value}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="scf-result__footer">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setResult(null)}
                        >
                            {t(`${PREFIX}.clearResult`)}
                        </Button>
                    </div>
                </section>
            )}

            <Modal
                show={showConfirm}
                onHide={() => !loading && setShowConfirm(false)}
                centered
                className="scf-confirm-modal"
            >
                <Modal.Header closeButton={!loading}>
                    <Modal.Title>
                        <FiEye className="me-2" aria-hidden />
                        {t(`${PREFIX}.confirmTitle`)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">{t(`${PREFIX}.confirmMessage`)}</p>
                </Modal.Body>
                <Modal.Footer className="scf-confirm-modal__footer">
                    <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={() => setShowConfirm(false)}
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        className="btn scf-btn-primary"
                        onClick={runCheck}
                        disabled={loading || !canRun}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" animation="border" aria-hidden />
                                {t(`${PREFIX}.running`)}
                            </>
                        ) : (
                            <>
                                <FiPlay size={16} aria-hidden />
                                {t(`${PREFIX}.confirmRun`)}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StandardBulkObservationCheckTab;
